import React, { useEffect, useState } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import {
  Bars3Icon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronDoubleRightIcon,
  HomeIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import {
  ArrowUpCircleIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import { ChevronDoubleDownIcon as ChevronDoubleDownIcon16 } from "@heroicons/react/16/solid";
import CourseViewer from "./courseviewquiz";
import LoadingComponent from "./loading";

const navigation = [{ name: "Dashboard", href: "/dashboard", icon: HomeIcon, current: true }];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function QuizBuilder() {
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState("");
  const [newOptions, setNewOptions] = useState(["", "", "", ""]);
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [trainingDocuments, setTrainingDocuments] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [manual, setManual] = useState(true);
  const [numberOfQuestions, setNumberOfQuestions] = useState(0);
  const [difficulty, setDifficulty] = useState("Easy");
  const [loading, setLoading] = useState(false);
  const [aiData, setAIData] = useState([]);
  const [message, setMessage] = useState("");
  const [existingQuiz, setExistingQuiz] = useState([]);
  const [openDeleteDifficulty, setOpenDeleteDifficulty] = useState(null);
  const [reload, setReload] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(true);

  const selectedTrainingId = selectedDocument?.pages?.[0]?.id;
  const hasExistingQuiz = existingQuiz.some((group) => group.Difficulty !== "No Quiz");

  const fetchTrainingDocuments = async () => {
    try {
      const response = await fetch("https://api.insightos.co.uk/api/trainingdocuments", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok) {
        const groupedDocuments = data.data.reduce((acc, document) => {
          const existingGroup = acc.find((group) => group.TrainingDocName === document.TrainingDocName);

          if (existingGroup) {
            existingGroup.pages.push(document);
            existingGroup.pages.sort((a, b) => a.Page - b.Page);
          } else {
            acc.push({ TrainingDocName: document.TrainingDocName, pages: [document] });
          }
          return acc;
        }, []);

        groupedDocuments.sort((a, b) => a.TrainingDocName.localeCompare(b.TrainingDocName));
        setTrainingDocuments(groupedDocuments);
      } else {
        console.error("Error fetching training documents:", data);
      }
    } catch (error) {
      console.error("Error fetching training documents:", error);
    }
  };

  useEffect(() => {
    fetchTrainingDocuments();
  }, []);

  useEffect(() => {
    const fetchExistingQuestions = async () => {
      if (!selectedDocument?.pages?.[0]?.id) return;

      try {
        const response = await fetch(`https://api.insightos.co.uk/api/getquiz/${selectedDocument.pages[0].id}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        const data = await response.json();
        const questionsData = data.data || [];

        if (response.ok) {
          if (questionsData.length === 0) {
            setExistingQuiz([{ Difficulty: "No Quiz", questions: [null] }]);
            return;
          }

          const groupedQuestions = questionsData.reduce((acc, question) => {
            const existingGroup = acc.find((group) => group.Difficulty === question.Difficulty);

            if (existingGroup) {
              existingGroup.questions.push(question);
            } else {
              acc.push({
                Difficulty: question.Difficulty,
                questions: [question],
                answer: question.Answer,
                current: question.Current,
                id: question.id,
                created: question.CreatedAt,
                author: question.Author,
              });
            }
            return acc;
          }, []);

          setExistingQuiz(groupedQuestions);
        } else {
          console.error("Error fetching existing questions:", data);
        }
      } catch (error) {
        console.error("Error fetching existing questions:", error);
      }
    };

    fetchExistingQuestions();
  }, [selectedDocument, reload]);

  const addQuestion = () => {
    if (!newQuestion || !correctAnswer || newOptions.some((option) => !option.trim())) {
      setMessage("Fill all question fields before adding it.");
      return;
    }

    const formattedQuestion = `${questions.length + 1}. ${newQuestion}`;
    const formattedOptions = newOptions.map((option, index) => `${index + 1}. ${option}`);
    const correctAnswerNumber = newOptions.indexOf(correctAnswer) + 1;

    setQuestions([
      ...questions,
      {
        question: formattedQuestion,
        options: formattedOptions,
        correctAnswer: `${correctAnswerNumber}. ${correctAnswer}`,
      },
    ]);

    setNewQuestion("");
    setNewOptions(["", "", "", ""]);
    setCorrectAnswer("");
    setMessage("Question added to the draft quiz.");
  };

  const handleInputChange = (e) => {
    setManual(e.target.value !== "AI Generated");
  };

  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const waitForAiQuizJob = async (jobId) => {
    for (let attempt = 0; attempt < 120; attempt += 1) {
      const res = await fetch(`https://api.insightos.co.uk/api/redisjobs/${jobId}`, {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to check quiz job status.");

      if (data.status === "completed") return data.result || null;
      if (data.status === "failed") throw new Error(data.failedReason || "Quiz generation job failed.");

      await sleep(1500);
    }
    throw new Error("Timed out waiting for AI quiz generation.");
  };

  const generateQuestions = async (e) => {
    e.preventDefault();

    if (!selectedDocument) {
      setMessage("Select a training document first.");
      return;
    }

    setLoading(true);
    setMessage("Generating questions…");
    try {
      const content = selectedDocument.pages.map((page) => page.Content).join(" ");

      const response = await fetch("https://api.insightos.co.uk/api/aiquiz", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          content,
          numberOfQuestions,
          difficulty,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setMessage(data.message || "Error generating questions.");
        return;
      }

      let result = data;

      if (response.status === 202 && data.queued && data.jobId) {
        setMessage("Quiz queued — waiting for AI to finish…");
        result = await waitForAiQuizJob(data.jobId);
      }

      if (result && result.success) {
        const quizContent = result.quiz;
        const regex = /<<QUESTION>>\n(.*?)\n<<NUMBER>>\n(.*?)\n(.*?)\n(.*?)\n(.*?)\n<<ANSWER>>\n(.*?)(?=\n<<QUESTION>>|$)/gs;

        const extractedQuestions = [];
        let match;
        while ((match = regex.exec(quizContent)) !== null) {
          extractedQuestions.push({
            question: match[1],
            options: [match[2], match[3], match[4], match[5]],
            correctAnswer: match[6],
          });
        }

        setAIData(extractedQuestions);
        setMessage("AI questions generated successfully.");
      } else {
        setMessage((result && result.message) || "Error generating questions.");
      }
    } catch (error) {
      console.error("Network or Server Error:", error);
      setMessage(error.message || "Network or server error while generating questions.");
    } finally {
      setLoading(false);
    }
  };

  const saveAIQuestions = async () => {
    if (!selectedTrainingId || aiData.length === 0) {
      setMessage("Generate questions before saving.");
      return;
    }

    try {
      const questionsToSave = aiData.map((q) => ({
        question: q.question,
        options: q.options,
        correctAnswer: q.correctAnswer,
      }));

      const response = await fetch("https://api.insightos.co.uk/api/saveaiquiz", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          questions: questionsToSave,
          TrainingId: selectedTrainingId,
          Difficulty: difficulty,
          Current: hasExistingQuiz ? 0 : 1,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage("Quiz saved successfully.");
        fetchTrainingDocuments();
        setReload((prev) => !prev);
      } else {
        setMessage("Error saving quiz. Please try again.");
      }
    } catch (error) {
      console.error("Error saving quiz:", error);
      setMessage("Error saving quiz. Please try again.");
    }
  };

  const saveManualQuestions = async () => {
    if (!selectedTrainingId || questions.length === 0) {
      setMessage("No questions to save.");
      return;
    }

    try {
      const response = await fetch("https://api.insightos.co.uk/api/saveaiquiz", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          questions,
          TrainingId: selectedTrainingId,
          Difficulty: difficulty,
          Current: hasExistingQuiz ? 0 : 1,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage("Questions saved successfully.");
        fetchTrainingDocuments();
        setReload((prev) => !prev);
      } else {
        setMessage(data.message || "Error saving questions.");
      }
    } catch (error) {
      console.error("Error saving questions:", error);
      setMessage("Error saving questions.");
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/deletequestion/${questionId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok && data.message === "Question deleted successfully") {
        setReload((prev) => !prev);
        setMessage("Question deleted.");
      } else {
        setMessage(data.message || "Error deleting question.");
      }
    } catch (error) {
      console.error("Error deleting question:", error);
      setMessage("Error deleting question.");
    }
  };

  const handleMakeCurrent = async (quizDifficulty) => {
    if (!selectedTrainingId) return;

    try {
      const response = await fetch(`https://api.insightos.co.uk/api/makecurrent/${selectedTrainingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ Difficulty: quizDifficulty }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setReload((prev) => !prev);
        setMessage("Quiz marked as current.");
      } else {
        setMessage(data.message || "Error making quiz current.");
      }
    } catch (error) {
      console.error("Error making quiz current:", error);
      setMessage("Error making quiz current.");
    }
  };

  const handleDeleteQuiz = async (quizDifficulty) => {
    if (!selectedTrainingId) return;

    try {
      const response = await fetch(`https://api.insightos.co.uk/api/deletequiz/${selectedTrainingId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ Difficulty: quizDifficulty }),
      });

      const data = await response.json();

      if (response.ok && data.message === "Quiz deleted successfully") {
        setReload((prev) => !prev);
        setMessage("Quiz deleted.");
      } else {
        setMessage(data.message || "Error deleting quiz.");
      }
    } catch (error) {
      console.error("Error deleting quiz:", error);
      setMessage("Error deleting quiz.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Dialog open={sidebarOpen} onClose={() => setSidebarOpen(false)} className="relative z-50 lg:hidden">
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-y-0 left-0 w-full max-w-xs bg-gradient-to-b from-indigo-900 via-sky-500 to-emerald-500 shadow-2xl shadow-indigo-950/40">
          <DialogPanel className="flex h-full flex-col px-4 pb-4">
            <div className="flex h-16 items-center justify-between border-b border-white/15">
              <img alt="Your Company" src="2.png" className="h-10 w-auto rounded-full bg-white/95 p-1 shadow-lg shadow-indigo-950/30" />
              <button type="button" className="rounded-md p-2.5 text-white/90 hover:bg-white/10" onClick={() => setSidebarOpen(false)}>
                <span className="sr-only">Close sidebar</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            {loading && <LoadingComponent />}

            <nav className="mt-5 flex flex-1 flex-col gap-y-6 overflow-y-auto">
              <ul role="list" className="space-y-1">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.href}
                      className={classNames(
                        item.current ? "bg-white text-indigo-700 shadow-lg shadow-indigo-950/20" : "text-white/95 hover:bg-white/10",
                        "group flex gap-x-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors"
                      )}
                    >
                      <item.icon className={classNames(item.current ? "text-indigo-700" : "text-white", "h-6 w-6 shrink-0")} />
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>

              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">Training Documents</div>
                <ul role="list" className="space-y-1">
                  {trainingDocuments.map((docGroup) => (
                    <li key={docGroup.TrainingDocName}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedDocument(docGroup);
                          setSidebarOpen(false);
                        }}
                        className={classNames(
                          selectedDocument?.TrainingDocName === docGroup.TrainingDocName
                            ? "bg-white text-indigo-700 shadow-lg shadow-indigo-950/20"
                            : "text-white/95 hover:bg-white/10",
                          "group flex w-full gap-x-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors"
                        )}
                      >
                        <DocumentDuplicateIcon className={classNames(selectedDocument?.TrainingDocName === docGroup.TrainingDocName ? "text-indigo-700" : "text-white", "h-6 w-6 shrink-0")} />
                        <span className="truncate">{docGroup.TrainingDocName}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </nav>
          </DialogPanel>
        </div>
      </Dialog>

      <aside className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gradient-to-b from-indigo-900 via-sky-500 to-emerald-500 px-5 pb-4 shadow-2xl shadow-indigo-950/35 border-r border-white/10">
          <div className="flex h-16 shrink-0 items-center justify-center border-b border-white/15">
            <img alt="Your Company" src="2.png" className="h-11 w-auto rounded-full bg-white/95 p-1 shadow-lg shadow-indigo-950/30" />
          </div>

          {loading && <LoadingComponent />}

          <nav className="flex flex-1 flex-col gap-y-6">
            <ul role="list" className="space-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  <a
                    href={item.href}
                    className={classNames(
                      item.current ? "bg-white text-indigo-700 shadow-lg shadow-indigo-950/20" : "text-white/95 hover:bg-white/10",
                      "group flex gap-x-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors"
                    )}
                  >
                    <item.icon className={classNames(item.current ? "text-indigo-700" : "text-white", "h-6 w-6 shrink-0")} />
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">Training Documents</div>
              <ul className="space-y-1">
                {trainingDocuments.map((docGroup) => (
                  <li key={docGroup.TrainingDocName}>
                    <button
                      type="button"
                      onClick={() => setSelectedDocument(docGroup)}
                      className={classNames(
                        selectedDocument?.TrainingDocName === docGroup.TrainingDocName
                          ? "bg-white text-indigo-700 shadow-lg shadow-indigo-950/20"
                          : "text-white/95 hover:bg-white/10",
                        "group flex w-full gap-x-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors"
                      )}
                    >
                      <DocumentDuplicateIcon className={classNames(selectedDocument?.TrainingDocName === docGroup.TrainingDocName ? "text-indigo-700" : "text-white", "h-6 w-6 shrink-0")} />
                      <span className="truncate">{docGroup.TrainingDocName}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        </div>
      </aside>

      <div className="lg:pl-72">
        <header className="sticky top-0 z-40 border-b border-white/20 bg-gradient-to-r from-indigo-900 via-sky-500 to-emerald-500 px-4 shadow-lg shadow-indigo-950/20 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center gap-x-4">
            <button type="button" onClick={() => setSidebarOpen(true)} className="-m-2.5 rounded-lg p-2.5 text-white lg:hidden">
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>

            <div className="flex min-w-0 flex-1 items-center justify-between gap-4">
              <div className="min-w-0">
                <h1 className="truncate text-lg font-semibold text-white sm:text-xl">Quiz Builder</h1>
                <p className="truncate text-xs text-white/80">Professional quiz authoring and review for training documents</p>
              </div>

              <div className="relative hidden w-full max-w-xs sm:block">
                <select
                  onChange={handleInputChange}
                  id="location"
                  name="location"
                  defaultValue="Manual"
                  className="w-full appearance-none rounded-2xl border border-white/20 bg-white/95 py-2 pl-4 pr-11 text-sm font-semibold text-slate-800 shadow-lg shadow-indigo-950/15 outline-none transition focus:ring-2 focus:ring-white/60"
                >
                  <option value="Manual">Manual</option>
                  <option value="AI Generated">AI Generated</option>
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 size-5 -translate-y-1/2 text-slate-500" />
              </div>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          {message && (
            <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50/95 px-4 py-3 text-sm font-semibold text-emerald-700 shadow-lg shadow-emerald-900/10 backdrop-blur">
              {message}
            </div>
          )}

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
            <section className="space-y-6">
              <div className="rounded-2xl border border-white/40 bg-white/75 p-5 shadow-xl shadow-indigo-900/10 backdrop-blur-xl sm:p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Current document</p>
                    <h2 className="mt-1 text-xl font-bold text-slate-800 sm:text-2xl">
                      {selectedDocument ? (selectedDocument.Title || selectedDocument.TrainingDocName) : "Select a training document"}
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">Build, review, and manage quizzes from one clean workspace.</p>
                  </div>

                  {selectedDocument && (
                    <div className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white shadow-lg shadow-slate-900/20">
                      {selectedDocument.pages?.length || 0} pages loaded
                    </div>
                  )}
                </div>
              </div>

              {selectedDocument ? (
                <div className="rounded-2xl border border-white/40 bg-white/75 shadow-xl shadow-indigo-900/10 backdrop-blur-xl">
                  <button
                    type="button"
                    onClick={() => setPreviewOpen((o) => !o)}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-200/80 px-5 py-4 text-left sm:px-6"
                  >
                    <div>
                      <h3 className="text-base font-semibold text-slate-800">Document preview</h3>
                      <p className="text-sm text-slate-600">Use this panel to read the source content before writing questions.</p>
                    </div>
                    <ChevronDownIcon
                      className={classNames(
                        previewOpen ? "rotate-180" : "rotate-0",
                        "h-5 w-5 shrink-0 text-slate-500 transition-transform duration-200"
                      )}
                    />
                  </button>
                  {previewOpen && (
                    <div className="p-3 sm:p-4">
                      <CourseViewer courseDocName={selectedDocument.TrainingDocName} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 p-8 text-center shadow-lg shadow-indigo-900/5 backdrop-blur-xl">
                  <h3 className="text-lg font-semibold text-slate-800">Choose a training document to begin</h3>
                  <p className="mt-2 text-sm text-slate-600">The document list on the left drives the quiz builder. Once selected, the preview and editor will appear here.</p>
                </div>
              )}

              {selectedDocument && (
                <div className="rounded-2xl border border-white/40 bg-white/75 shadow-xl shadow-indigo-900/10 backdrop-blur-xl">
                  <div className="border-b border-slate-200/80 px-5 py-4 sm:px-6">
                    <h3 className="text-base font-semibold text-slate-800">Existing quizzes</h3>
                    <p className="text-sm text-slate-600">Review, promote, or delete the quizzes already linked to this document.</p>
                  </div>

                  <div className="space-y-4 p-5 sm:p-6">
                    {existingQuiz.map((group, index) => (
                      <div key={index} className="rounded-2xl border border-white/60 bg-white/90 p-4 shadow-lg shadow-indigo-900/10">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <h4 className="text-lg font-bold text-indigo-700">Quiz: {group.Difficulty}</h4>
                            <p className="text-sm text-slate-600">Author: {group.author || "Unknown"}</p>
                          </div>

                          {group.current === 1 ? (
                            <span className="inline-flex items-center gap-x-2 rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-indigo-900/20">
                              Current Quiz
                              <CheckCircleIcon aria-hidden="true" className="size-4" />
                            </span>
                          ) : (
                            <button
                              onClick={() => handleMakeCurrent(group.Difficulty)}
                              className="inline-flex items-center gap-x-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-emerald-900/20 transition hover:bg-emerald-500"
                            >
                              <ChevronDoubleRightIcon className="h-4 w-4" />
                              Make Current
                            </button>
                          )}
                        </div>

                        {group.Difficulty !== "No Quiz" && (
                          <div className="mt-4 flex flex-wrap gap-3">
                            {openDeleteDifficulty !== group.Difficulty ? (
                              <button
                                onClick={() => setOpenDeleteDifficulty(openDeleteDifficulty === group.Difficulty ? null : group.Difficulty)}
                                className="inline-flex items-center gap-x-1.5 rounded-full bg-slate-900 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-slate-900/20 transition hover:bg-slate-800"
                              >
                                <ChevronDoubleDownIcon16 className="h-5 w-5" />
                                Show Questions
                              </button>
                            ) : (
                              <button
                                onClick={() => setOpenDeleteDifficulty(null)}
                                className="inline-flex items-center gap-x-1.5 rounded-full bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-900/20 transition hover:bg-orange-500"
                              >
                                <ArrowUpCircleIcon className="h-5 w-5" />
                                Hide Questions
                              </button>
                            )}

                            <button
                              onClick={() => handleDeleteQuiz(group.Difficulty)}
                              className="inline-flex items-center gap-x-1.5 rounded-full bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-red-900/20 transition hover:bg-red-500"
                            >
                              <XCircleIcon className="h-5 w-5" />
                              Delete Quiz
                            </button>
                          </div>
                        )}

                        {openDeleteDifficulty === group.Difficulty && (
                          <div className="mt-4 space-y-3 rounded-2xl bg-slate-50/90 p-4">
                            {group.questions.map((question, idx) => (
                              <div key={idx} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                                <p className="font-medium text-slate-800">{question.Question}?</p>
                                <p className="mt-1 text-sm text-slate-600">Answer: {question.Answer}</p>
                                <button
                                  onClick={() => handleDeleteQuestion(question.id)}
                                  className="mt-3 inline-flex items-center gap-x-1.5 rounded-full bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-red-900/20 transition hover:bg-red-500"
                                >
                                  Delete
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <aside className="space-y-6">
              {manual && selectedDocument && (
                <div className="rounded-2xl border border-white/40 bg-white/75 p-5 shadow-xl shadow-indigo-900/10 backdrop-blur-xl sm:p-6">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Manual mode</p>
                      <h3 className="mt-1 text-lg font-semibold text-slate-800">Create a question set</h3>
                      <p className="mt-1 text-sm text-slate-600">Write questions directly with controlled options and a clear answer key.</p>
                    </div>
                    <div className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">Difficulty: {difficulty}</div>
                  </div>

                  {hasExistingQuiz && (
                    <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      An existing quiz already exists. Using the same difficulty appends questions; changing difficulty creates a new quiz.
                    </p>
                  )}

                  <div className="space-y-3 rounded-2xl bg-slate-50/90 p-4">
                    <label htmlFor="difficulty" className="block text-sm font-medium text-slate-700">Difficulty</label>
                    <select
                      id="difficulty"
                      onChange={(e) => setDifficulty(e.target.value)}
                      name="difficulty"
                      required
                      className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                    >
                      <option value="Easy">Easy</option>
                      <option value="Medium">Medium</option>
                      <option value="Hard">Hard</option>
                    </select>

                    <label htmlFor="question-input" className="block text-sm font-medium text-slate-700">Question</label>
                    <input
                      id="question-input"
                      type="text"
                      placeholder="Enter question"
                      value={newQuestion}
                      onChange={(e) => setNewQuestion(e.target.value)}
                      className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                    />

                    {newOptions.map((opt, index) => (
                      <input
                        key={index}
                        type="text"
                        placeholder={`Option ${index + 1}`}
                        value={opt}
                        onChange={(e) => setNewOptions(newOptions.map((option, optionIndex) => (optionIndex === index ? e.target.value : option)))}
                        className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                      />
                    ))}

                    <select
                      value={correctAnswer}
                      onChange={(e) => setCorrectAnswer(e.target.value)}
                      className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                    >
                      <option value="">Select Correct Answer</option>
                      {newOptions.map((opt, index) => (
                        <option key={index} value={opt}>{opt}</option>
                      ))}
                    </select>

                    <button
                      onClick={addQuestion}
                      className="inline-flex w-full items-center justify-center rounded-full bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-900/20 transition hover:bg-indigo-500"
                      data-component="quiz-builder-manual"
                      data-feature="add_question_local"
                    >
                      Add Question
                    </button>
                  </div>

                  <div className="mt-5 space-y-3">
                    <h4 className="text-base font-semibold text-slate-800">Preview</h4>
                    {questions.length > 0 ? (
                      questions.map((q, index) => (
                        <div key={index} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                          <p className="font-medium text-slate-900">{q.question}?</p>
                          <div className="mt-3 space-y-2">
                            {q.options.map((option, idx) => {
                              const isCorrect = option === q.correctAnswer;
                              return (
                                <div
                                  key={idx}
                                  className={classNames(
                                    isCorrect ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-50 text-slate-700",
                                    "flex items-center gap-2 rounded-xl border px-3 py-2 text-sm"
                                  )}
                                >
                                  <input type="radio" name={`question-${index + 1}`} value={option} className="accent-indigo-600" disabled />
                                  <span className="font-medium">{option}</span>
                                </div>
                              );
                            })}
                          </div>
                          <p className="mt-3 text-sm font-semibold text-emerald-700">Correct answer: {q.correctAnswer}</p>
                        </div>
                      ))
                    ) : (
                      <p className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">No manual questions added yet.</p>
                    )}

                    <button
                      onClick={saveManualQuestions}
                      className="inline-flex w-full items-center justify-center rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-900/20 transition hover:bg-emerald-500"
                      data-component="quiz-builder-manual"
                      data-feature="save_manual_quiz_post"
                      data-training-id={selectedTrainingId}
                    >
                      Save Manual Questions
                    </button>
                  </div>
                </div>
              )}

              {!manual && (
                <div className="rounded-2xl border border-white/40 bg-white/75 p-5 shadow-xl shadow-indigo-900/10 backdrop-blur-xl sm:p-6">
                  <div className="mb-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">AI mode</p>
                    <h3 className="mt-1 text-lg font-semibold text-slate-800">Generate quiz questions</h3>
                    <p className="mt-1 text-sm text-slate-600">Create a structured set of questions from the selected document content.</p>
                  </div>

                  <form onSubmit={generateQuestions} className="space-y-4 rounded-2xl bg-slate-50/90 p-4">
                    <div>
                      <label htmlFor="question-count" className="block text-sm font-medium text-slate-700">Number of questions</label>
                      <input
                        type="number"
                        onChange={(e) => setNumberOfQuestions(e.target.value)}
                        id="question-count"
                        name="question"
                        min="1"
                        required
                        className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                      />
                    </div>

                    <div>
                      <label htmlFor="difficulty-ai" className="block text-sm font-medium text-slate-700">Difficulty</label>
                      <select
                        id="difficulty-ai"
                        onChange={(e) => setDifficulty(e.target.value)}
                        name="difficulty"
                        required
                        className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                      >
                        <option value="Easy">Easy</option>
                        <option value="Medium">Medium</option>
                        <option value="Hard">Hard</option>
                      </select>
                    </div>

                    <button
                      type="submit"
                      className="inline-flex w-full items-center justify-center rounded-full bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-900/20 transition hover:bg-indigo-500"
                      data-component="quiz-builder-ai"
                      data-time-saved="180"
                      data-feature="generate_ai_quiz_post"
                    >
                      Generate Questions
                    </button>
                  </form>

                  <div className="mt-5 space-y-3">
                    <h4 className="text-base font-semibold text-slate-800">Generated questions</h4>
                    {aiData.length > 0 ? (
                      aiData.map((q, index) => (
                        <div key={index} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                          <p className="text-sm font-semibold text-slate-900">
                            <span className="text-indigo-600">Q{index + 1}:</span> {q.question}
                          </p>
                          <div className="mt-3 space-y-2">
                            {q.options.map((option, idx) => {
                              const isCorrect = option === q.correctAnswer;
                              return (
                                <label
                                  key={idx}
                                  className={classNames(
                                    isCorrect ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50",
                                    "flex items-center gap-2 rounded-xl border px-3 py-2 text-sm text-slate-700"
                                  )}
                                >
                                  <input type="radio" name={`question-${index + 1}`} value={option} className="accent-indigo-600" disabled />
                                  <span className={classNames(isCorrect ? "text-emerald-700" : "text-slate-900", "font-medium")}>
                                    {String.fromCharCode(65 + idx)}. {option}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                          <p className="mt-3 text-sm font-semibold text-emerald-700">Correct answer: {q.correctAnswer}</p>
                        </div>
                      ))
                    ) : (
                      <p className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">No questions generated yet.</p>
                    )}

                    <button
                      onClick={saveAIQuestions}
                      className="inline-flex w-full items-center justify-center rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-900/20 transition hover:bg-emerald-500"
                      data-component="quiz-builder-ai"
                      data-feature="save_ai_quiz_post"
                      data-training-id={selectedTrainingId}
                    >
                      Save AI Generated Questions
                    </button>
                    <button
                      onClick={() => setAIData([])}
                      className="inline-flex w-full items-center justify-center rounded-full bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-red-900/20 transition hover:bg-red-500"
                      data-component="quiz-builder-ai"
                      data-feature="cancel_ai_quiz"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </aside>
          </div>
        </main>
      </div>
    </div>
  );
}